import React, { useEffect, useState } from 'react';
import { Box, Grid, InputAdornment, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { GridRowModes, GridToolbarContainer, useGridApiRef, GridActionsCellItem } from '@mui/x-data-grid-premium';
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuidv4 } from 'uuid';

import { ButtonUI, DataGridIdentifiers, DatePickerUI, DialogUI, InputUI } from '../Interface';
import { formatCurrency } from '../../utils/currencyHelper';
import DataGridPremiumUI from '../Interface/DatagridPremmium/DataGridPremiumUI';
import AddButtonUI from '../Interface/AddButtonUI';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import projectService from '../../service/project.service';
import { getInputStepByDecimalPlaces, roundToDecimalPlaces } from '../../utils/numberFormatHelper';
import useParam from '../../context/ParamContext';

const EditToolbar = (props) => {
  const { data, rows, setRows, setChangedRows } = props;

  const handleClick = async () => {
    // Legnagyobb nap meghatározása az összes meglévő dátumból (validFrom és validTo), majd +1 nap
    const candidates = [];
    (rows || []).forEach((r) => {
      if (r?.validFrom) candidates.push(dayjs(r.validFrom).startOf('day'));
      if (r?.validTo) candidates.push(dayjs(r.validTo).startOf('day'));
    });
    const base = candidates.length
      ? candidates.reduce((max, d) => (d.isAfter(max) ? d : max), candidates[0])
      : dayjs().startOf('day');
    const defaultStart = (candidates.length ? base.add(1, 'day') : base).startOf('day').add(12, 'hours').toISOString();

    const newField = {
      isNew: true,
      id: uuidv4(),
      itemCode: data.itemCode,
      projectId: data.projectId,
      validFrom: defaultStart,
      validTo: null,
      price: null,
      direction: data.direction,
      currencyCode: data.currencyCode,
    };

    setRows((oldRows) => [...oldRows, newField]);
    setChangedRows((old) => [...old, newField]);
  };

  return (
    <GridToolbarContainer>
      <AddButtonUI onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const ProjectPriceDialog = ({ onClose, data, setRefetchTable, setSelectedValues }) => {
  const { open } = data;
  const { params } = useParam();
  const apiRef = useGridApiRef();

  const [rows, setRows] = useState([]);
  const [changedRows, setChangedRows] = useState([]);

  // Validáció: kötelező mezők és dátum átfedések ellenőrzése
  const validateChanges = (changes, allRows) => {
    const END_OF_TIME = dayjs('9999-12-31').endOf('day');
    const toDay = (d) => (d ? dayjs(d) : null);

    const getRange = (r) => {
      const from = toDay(r.validFrom)?.startOf('day') || null;
      const to = r.validTo ? toDay(r.validTo).endOf('day') : END_OF_TIME; // nyitott végű tartomány
      return { from, to };
    };

    const rangesOverlap = (a, b) => {
      if (!a.from || !b.from) return true;
      return !(a.from.isAfter(b.to) || b.from.isAfter(a.to));
    };

    const formatRow = (r) =>
      `${dayjs(r.validFrom).format('YYYY.MM.DD')} - ${r.validTo ? dayjs(r.validTo).format('YYYY.MM.DD') : 'nyitott'}`;

    const activeChanges = changes.filter((r) => !r.deleted);
    const activeAllRows = allRows.filter((r) => !r.deleted);

    // Kötelező mezők és belső konzisztencia (első hiba esetén kilép)
    for (const r of activeChanges) {
      if (!r.validFrom) return { valid: false, messages: ['Hiányzik a kezdő dátum (Kezdete).'] };
      const priceNum = r.price === '' || r.price === null || r.price === undefined ? NaN : Number(r.price);
      if (!Number.isFinite(priceNum)) return { valid: false, messages: ['Hiányzik vagy érvénytelen az ár.'] };
      if (r.validFrom && r.validTo) {
        const from = toDay(r.validFrom).startOf('day');
        const to = toDay(r.validTo).endOf('day');
        if (to.isBefore(from))
          return {
            valid: false,
            messages: [
              `A befejezés (${to.format('YYYY.MM.DD')}) nem lehet korábbi, mint a kezdés (${from.format('YYYY.MM.DD')}).`,
            ],
          };
      }
    }

    // Átfedés ellenőrzés: changedRows vs. teljes lista (első hiba esetén kilép)
    for (const r of activeChanges) {
      const rRange = getRange(r);
      const comparable = (activeAllRows || []).filter(
        (x) => x.itemCode === r.itemCode && x.projectId === r.projectId && x.id !== r.id
      );
      for (const x of comparable) {
        if (rangesOverlap(rRange, getRange(x))) {
          return {
            valid: false,
            messages: [`Dátum átfedés észlelve: ${formatRow(r)} ütközik ezzel: ${formatRow(x)}.`],
          };
        }
      }
    }

    // Átfedés ellenőrzés: changedRows egymás között (első hiba esetén kilép)
    for (let i = 0; i < activeChanges.length; i++) {
      for (let j = i + 1; j < activeChanges.length; j++) {
        const a = activeChanges[i];
        const b = activeChanges[j];
        if (a.itemCode === b.itemCode && a.projectId === b.projectId) {
          if (rangesOverlap(getRange(a), getRange(b))) {
            return { valid: false, messages: [`A módosított sorok átfednek: ${formatRow(a)} és ${formatRow(b)}.`] };
          }
        }
      }
    }

    return { valid: true, messages: [] };
  };

  const getData = () => {
    projectService.getPrices(data.itemCode, data.projectId).then((data) => {
      setRows(data);
    });
  };

  useEffect(() => {
    if (!data.itemCode || !data.projectId) return;
    getData();
  }, [data]);

  const handleDialogClose = () => {
    setRows([]);
    setChangedRows([]);
    onClose();
  };

  const handleDeleteRow = (params) => {
    if (rows.length === 1) {
      addNotification({
        content: 'Az utolsó ár nem törölhető ebből az ablakból!',
        type: NotificationType.WARNING,
      });

      return;
    }
    const { id, row } = params;

    setRows((prev) => prev.filter((r) => r.id !== id));

    if (row.isNew) {
      setChangedRows((prev) => prev.filter((r) => r.id !== id));
      return;
    }

    setChangedRows((prev) => {
      const others = prev.filter((r) => r.id !== id);
      return [...others, { ...row, deleted: true }];
    });
  };

  const handleSave = () => {
    if (changedRows.length === 0) return handleDialogClose();

    const { valid, messages } = validateChanges(changedRows, rows);
    if (!valid) {
      addNotification({
        content: messages.join('\n'),
        type: NotificationType.ERROR,
      });

      return;
    }
    const saveData = changedRows.map((r) => ({
      id: r.isNew ? undefined : r.id,
      itemCode: r.itemCode,
      projectId: r.projectId,
      validFrom: dayjs(r.validFrom).startOf('day').add(12, 'hours').toISOString(),
      validTo: r.validTo ? dayjs(r.validTo).startOf('day').add(12, 'hours').toISOString() : null,
      direction: r.direction,
      currencyCode: r.currencyCode,
      price: roundToDecimalPlaces(r.price, data.priceRounding),
      deleted: r.deleted ?? false,
    }));

    projectService.savePrices(saveData).then((data) => {
      handleDialogClose();
      setRefetchTable(true);
      setSelectedValues(data);
      addNotification({
        content: 'Sikeres mentés!',
        type: NotificationType.SUCCESS,
      });
    });
  };
  const handleCancel = () => {
    handleDialogClose();
  };

  const handleFieldChange = async (field, newValue, row) => {
    const updatedRow = { ...row, [field]: newValue };
    setChangedRows((prev) => {
      const otherChanges = prev.filter((r) => r.id !== row.id);
      return [...otherChanges, updatedRow];
    });
    setRows((prev) => {
      const otherRows = prev.filter((r) => r.id !== row.id);
      return [...otherRows, updatedRow];
    });
  };

  const useQuantityPricing = params.PROJECT_PRICE_USE_QUANTITY === 'Y';

  const columns = () => [
    {
      field: 'validFrom',
      headerName: 'Kezdete',
      type: 'date',
      editable: true,
      valueGetter: (_data, row) => {
        return row.validFrom ? new Date(row.validFrom) : null;
      },
      renderCell: (data) => {
        return data.row.validFrom ? dayjs(data.row.validFrom).format('YYYY.MM.DD') : '';
      },
      renderEditCell: (data) => {
        return (
          <DatePickerUI
            name="validFrom"
            type="date"
            variant="standard"
            fullWidth
            value={data.row.validFrom ? dayjs(data.row.validFrom) : null}
            maxDate={data.row.validTo ? dayjs(data.row.validTo) : undefined}
            onChange={async (date) => {
              await handleFieldChange(data.field, date, data.row);
              data.api.setEditCellValue({
                id: data.id,
                field: data.field,
                value: date ? new Date(date) : null,
              });
            }}
          />
        );
      },
    },
    {
      field: 'validTo',
      headerName: 'Vége',
      type: 'date',
      editable: true,
      valueGetter: (_data, row) => {
        return row.validTo ? new Date(row.validTo) : null;
      },
      renderCell: (data) => {
        return data.row.validTo ? dayjs(data.row.validTo).format('YYYY.MM.DD') : '';
      },
      renderEditCell: (data) => {
        return (
          <DatePickerUI
            name="validTo"
            type="date"
            variant="standard"
            fullWidth
            value={data.row.validTo ? dayjs(data.row.validTo) : null}
            minDate={data.row.validFrom ? dayjs(data.row.validFrom) : undefined}
            onChange={async (date) => {
              await handleFieldChange(data.field, date, data.row);
              data.api.setEditCellValue({
                id: data.id,
                field: data.field,
                value: date ? new Date(date) : null,
              });
            }}
          />
        );
      },
    },
    {
      field: 'price',
      headerName: 'Ár',
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      renderCell: (rowData) => {
        return rowData.row.price ? `${formatCurrency(rowData.row.price, data.priceRounding)} ${data.currencyCode}` : '';
      },
      editable: true,
      renderEditCell: (rowData) => {
        return (
          <InputUI
            name="price"
            type="number"
            variant="standard"
            fullWidth
            value={rowData.row.price}
            precision={getInputStepByDecimalPlaces(data.priceRounding)}
            onChange={(e) => {
              const newValue = e.target.value ?? null;
              handleFieldChange(rowData.field, newValue, rowData.row);
              rowData.api.setEditCellValue({
                id: rowData.id,
                field: rowData.field,
                value: newValue,
              });
            }}
            InputProps={{ endAdornment: <InputAdornment position="end">{data.currencyCode}</InputAdornment> }}
          />
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 60,
      getActions: (params) => {
        return [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Törlés"
            onClick={() => handleDeleteRow(params)}
            showInMenu={false}
          />,
        ];
      },
    },
  ];

  return (
    <DialogUI open={open} onClose={handleDialogClose} headerContent={'Árlista kezelése'} maxWidth="md" fullWidth>
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={1} mb={2} sx={{ background: '#E9F5FE', borderRadius: '4px', p: '0.5rem' }}>
          {/* Left column: Dates */}
          <Grid item xs={12} md={9}>
            <Grid container spacing={0.5} alignItems="center">
              <Grid item xs={2}>
                <Typography variant="body2" color="text.secondary">
                  {useQuantityPricing ? 'Megnevezés' : 'Tevékenység'}
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography variant="body1" color="text.secondary">
                  {data.itemName}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={3}>
            <Grid container spacing={0.5} alignItems="center">
              <Grid item xs={4}>
                <Typography variant="body2" color="text.secondary">
                  Pénznem:
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="body1">{data.currencyCode}</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <DataGridPremiumUI
          apiRef={apiRef}
          datagridIdentifier={DataGridIdentifiers.ProjectPrice}
          height={480}
          columns={columns()}
          rows={rows}
          editMode="row"
          slots={{
            toolbar: EditToolbar,
          }}
          slotProps={{
            toolbar: {
              data,
              rows,
              setRows,
              setChangedRows,
            },
          }}
          onCellDoubleClick={(_params, event) => {
            event.defaultMuiPrevented = true;
          }}
          onCellClick={(params, event) => {
            const rowMode = apiRef.current.getRowMode(params.row.id);
            if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
          }}
          headerFilters={false}
          getRowHeight={() => 'auto'}
        />
      </Box>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <ButtonUI text="Mentés" className="bg-success" onClick={handleSave} />
        <ButtonUI text="Mégse" color="blue-gray" variant="gradient" onClick={handleCancel} />
      </Box>
    </DialogUI>
  );
};

export default ProjectPriceDialog;
